export const methylationModule = {
  id: "methylation",
  title: "Methylation Analysis",
  sections: [
    {
      type: "text",
      content: `<h3>Bisulfite-Sequencing</h3>
       <p>Bisulfite sequencing is a method used to detect DNA methylation. First the DNA is treated with sodium bisulfate. This causes unmethylated cytosine to convert to uracil via deamination while methylated cytosines stay unchanged. The DNA is thereafter amplified via PCR amplification where all the uracils are replaced with thymine. Both untreated and treated DNA from the same sample can then be sequenced and when comparing the sequences the thymine, representing the unmethylated cytosine, can easily be distinguished. This way methylated cytosine can be identified and studied. DNA methylation generally depresses gene transcription. DNA hydroxymethylation is known to activate gene expression and promotes DNA demethylation.</p>
       <div class="highlight-box">
       <p>Pros: Genome-wide coverage of methylation with high resolution (single-base resolution).</p>
       <p>Cons: Cannot distinguish between 5-methylcytosine (5mC) and 5-hydroxymethylcytosine (5hmC) methylation.</p>
       <p><em>Examples of uses in the field:</em>
       <ul><li>Glioblastoma patient with high methylation status at the MGMT promoter are more likely to respond to temozolomide treatment.</li></ul>
       </p>
       </div>      
       `,
      image: "/images/geneticMethods/Bisulfite.jpg",
    },
    {
      type: "question",
      text: "Methylation changes primarily affect:",
      options: [
        "Protein folding",
        "Gene expression",
        "RNA splicing",
        "DNA replication",
      ],
      correct: 1,
      explanation: "DNA methylation modulates gene expression.",
    },
    {
      type: "text",
      content: `<h3>Methylated DNA immunoprecipitation (MeDIP-Seq)</h3>
    <p>MeDIP-Seq is a method used to detect DNA methylation using antibodies. The DNA is fragmented by sonication, then denatured to allow for better antibody attachment. As the antibody needs more than just one methylated site for efficient binding, it is important that the DNA fragments are not to short. The DNA is exposed and incubated with 5mC (methylated cytosine) antibodies. Magnetic beads that have affinity for the antibody are then used to separate the methylated DNA strands from the unmethylated DNA strands. The methylated DNA strands can then be sequenced for further analysis.</p>
      
    <div class="highlight-box">
    <p>Pros: Does not detect 5hmC due to antibody specificity.</p>
    <p>Cons: The quality of the analysis is largely based on the specificity of the antibody used. Higher resolution is obtained with bisulfate-sequencing.</p>
    <p><em>Examples of uses in the field:</em>
    <ul><li>Studying methylation patterns in cancer research.</li></ul>
    </p>
    </div>
    `,
    },
    {
      type: "question",
      text: "Which of the following methods can distinguish between 5-methylcytosine (5mC) and 5-hydroxymethylcytosine (5hmC) methylation?",
      options: [
        "Bisulfite-Sequencing",
        "Methylated DNA immunoprecipitation (MeDIP-Seq)",
        "Both methods can distinguish between 5mC and 5hmC",
        "Neither method can distinguish between 5mC and 5hmC",
      ],
      correct: 1,
      explanation:
        "MeDIP-Seq uses antibodies specific to 5mC, allowing it to distinguish between 5mC and 5hmC, while bisulfite sequencing cannot differentiate between the two.",
    },
    {
      type: "question",
      text: "why is methylation analysis important in cancer research?",
      options: [
        "It helps identify mutations in oncogenes.",
        "It provides insights into gene expression regulation and potential therapeutic targets.",
        "It is used to determine the stage of cancer.",
        "It is not relevant to cancer research.",
      ],
      correct: 1,
      explanation:
        "Methylation analysis is crucial in cancer research as it helps understand gene expression regulation, identify potential therapeutic targets, and provide insights into the mechanisms of tumorigenesis.",
    },
  ],
};
